'use client';

import { Crown } from 'lucide-react';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { FeatureName } from '@/lib/feature-access';
import { useUsageLimitStore } from '@/lib/store/app';
import { paths } from '@/constants/paths';
import { hasPricingPage } from '@/constants/pricing';

import { appConfig } from '@/config/app';

const featureConfig: Record<
  FeatureName,
  {
    title: string;
    description: string;
  }
> = {
  'rank-tracker': {
    title: 'Rank Tracker Limit Reached. Please Upgrade',
    description:
      "You've reached the keyword limit for this plan. To track more keywords and gain access to additional features, please upgrade your subscription.",
  },
  geogrid: {
    title: 'GeoGrid Limit Reached. Please Upgrade',
    description:
      "You've reached the location tracking limit for this plan. To track more locations and gain access to additional features, please upgrade your subscription.",
  },
  keywords: {
    title: 'Keyword Research Limit Reached. Please Upgrade',
    description:
      "You've reached the keyword research limit for this plan. To research more keywords and gain access to additional features, please upgrade your subscription.",
  },
  'content-assistant': {
    title: 'Content Assistant Limit Reached. Please Upgrade',
    description:
      "You've reached the content generation limit for this plan. To create more content and gain access to additional features, please upgrade your subscription.",
  },
  backlinks: {
    title: 'Backlinks Limit Reached. Please Upgrade',
    description:
      "You've reached the backlink analysis limit for this plan. To analyze more backlinks and gain access to additional features, please upgrade your subscription.",
  },
  gsc: {
    title: 'Search Console Limit Reached. Please Upgrade',
    description:
      "You've reached the search console data limit for this plan. To access more data and gain access to additional features, please upgrade your subscription.",
  },
  'site-stats': {
    title: 'Site Stats Limit Reached. Please Upgrade',
    description:
      "You've reached the site stats limit for this plan. To access more stats and gain access to additional features, please upgrade your subscription.",
  },
  'site-audit': {
    title: 'Site Audit Limit Reached. Please Upgrade',
    description:
      "You've reached the site audit limit for this plan. To perform more audits and gain access to additional features, please upgrade your subscription.",
  },
};

export function LimitReachedModal() {
  const { onClose, featureName, open, customMessage } = useUsageLimitStore();
  if (!featureName) return null;
  const config = featureConfig[featureName] || {
    title: 'Feature Limit Reached. Please Upgrade',
    description:
      "You've reached the limit for this feature. Please upgrade to continue using this feature.",
  };

  const shouldShowUpgradeButton = hasPricingPage.includes(featureName);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader className='text-center'>
          <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
            <Crown className='h-6 w-6 text-primary' />
          </div>
          <DialogTitle className='text-center'>
            {customMessage?.title || config.title}
          </DialogTitle>
          <DialogDescription className='text-center'>
            {customMessage?.description || config.description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className=''>
          <div className='flex w-full justify-center gap-3'>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            {shouldShowUpgradeButton ? (
              <Link href={paths.pricing.feature(featureName)}>
                <Button onClick={onClose}>Upgrade</Button>
              </Link>
            ) : (
              <Link
                href={`mailto:${appConfig.email.support}?subject=Subscription Inquiry for ${featureName?.replace('-', ' ') || 'Feature'}`}
                onClick={onClose}
              >
                <Button>Contact Sales</Button>
              </Link>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
